import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  Select,
  Slider,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import {
  COLORS,
  DEFAULT_COMMENTS,
  DEFAULT_STAMPS,
  FONT_SIZES,
  MARK_VALUES,
} from "../lib/annotations";

function useStoredList(key, fallback) {
  const [items, setItems] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(key));
      return Array.isArray(saved) ? saved : fallback;
    } catch {
      return fallback;
    }
  });

  const save = (next) => {
    setItems(next);
    try {
      localStorage.setItem(key, JSON.stringify(next));
    } catch {
      // The list still works for this session if storage is blocked.
    }
  };

  return [items, save];
}

const Section = ({ title, children }) => (
  <Box sx={{ p: 1.75 }}>
    {title && (
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
        {title}
      </Typography>
    )}
    {children}
  </Box>
);

function ColorRow({ value, onChange, label }) {
  return (
    <Box display="flex" gap={0.75} role="radiogroup" aria-label={label}>
      {COLORS.map((color) => (
        <Box
          key={color}
          component="button"
          type="button"
          role="radio"
          aria-checked={value === color}
          aria-label={`Color ${color}`}
          onClick={() => onChange(color)}
          sx={{
            width: 26,
            height: 26,
            p: 0,
            border: "2px solid #fff",
            borderRadius: "50%",
            bgcolor: color,
            cursor: "pointer",
            boxShadow:
              value === color ? `0 0 0 2px ${color}` : "0 0 0 1px #d5deea",
          }}
        />
      ))}
    </Box>
  );
}

function AddItem({ label, onAdd }) {
  const [text, setText] = useState("");

  const add = () => {
    if (text.trim()) onAdd(text.trim());
    setText("");
  };

  return (
    <Box display="flex" gap={0.75} mt={1.25}>
      <TextField
        fullWidth
        size="small"
        value={text}
        placeholder={label}
        inputProps={{ "aria-label": label, maxLength: 120 }}
        onChange={(event) => setText(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") add();
        }}
      />
      <IconButton color="primary" aria-label={label} onClick={add}>
        <AddIcon />
      </IconButton>
    </Box>
  );
}

function PresetList({ items, selected, disabled, onPick, onChange, addLabel }) {
  return (
    <>
      {items.map((item) => (
        <Box key={item} display="flex" alignItems="center" sx={{ mb: 0.25 }}>
          <Button
            fullWidth
            size="small"
            color="inherit"
            disabled={disabled}
            onClick={() => onPick(item)}
            sx={{
              justifyContent: "flex-start",
              textAlign: "left",
              textTransform: "none",
              bgcolor: selected === item ? "#e8f1ff" : "transparent",
            }}
          >
            {item}
          </Button>
          <IconButton
            size="small"
            aria-label={`Delete ${item}`}
            disabled={disabled}
            onClick={() => onChange(items.filter((entry) => entry !== item))}
          >
            <DeleteOutlineIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Box>
      ))}
      {!disabled && (
        <AddItem
          label={addLabel}
          onAdd={(text) => {
            if (!items.includes(text)) onChange([...items, text]);
          }}
        />
      )}
    </>
  );
}

function MarkStamps({
  tool,
  options,
  activeQuestion,
  disabled,
  onTool,
  onOptions,
}) {
  return (
    <>
      <Box display="flex" flexWrap="wrap" gap={0.5} mb={1}>
        {MARK_VALUES.map((value) => (
          <Chip
            key={value}
            size="small"
            label={value}
            disabled={disabled}
            color={
              tool === "mark" && options.markValue === value
                ? "primary"
                : "default"
            }
            onClick={() => {
              onOptions({ markValue: value });
              onTool("mark");
            }}
          />
        ))}
      </Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {activeQuestion
          ? `Circle marks add up into Q${activeQuestion.no} and replace a typed mark.`
          : "Select a question to use circle marks."}
      </Typography>
    </>
  );
}

export default function ToolsPanel({
  tool,
  options,
  activeQuestion,
  readOnly,
  onTool,
  onOptions,
}) {
  const [tab, setTab] = useState(0);
  const [comments, setComments] = useStoredList(
    "osm_quick_comments",
    DEFAULT_COMMENTS,
  );
  const [stamps, setStamps] = useStoredList(
    "osm_custom_stamps",
    DEFAULT_STAMPS,
  );

  const textStyle = [
    options.bold && "bold",
    options.italic && "italic",
    options.underline && "underline",
  ].filter(Boolean);

  const pickComment = (commentText) => {
    onOptions({ commentText });
    onTool("comment");
  };

  const pickStamp = (stampText) => {
    onOptions({ stampText });
    onTool("stamp");
  };

  return (
    <Box
      sx={{
        width: 286,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        bgcolor: "#fff",
        borderLeft: "1px solid #dfe7f3",
      }}
    >
      <Tabs
        value={tab}
        variant="fullWidth"
        onChange={(_, value) => setTab(value)}
        sx={{ minHeight: 42, borderBottom: "1px solid #dfe7f3" }}
      >
        {["Tools", "Comments", "Stamps"].map((label) => (
          <Tab
            key={label}
            label={label}
            sx={{
              minHeight: 42,
              minWidth: 0,
              textTransform: "none",
              fontWeight: 600,
            }}
          />
        ))}
      </Tabs>

      <Box flex={1} overflow="auto">
        {tab === 0 && (
          <>
            <Section title="Drawing">
              <ColorRow
                label="Drawing color"
                value={options.color}
                onChange={(color) => onOptions({ color })}
              />
              <Box display="flex" alignItems="center" gap={1.5} mt={1.5}>
                <Typography variant="caption" color="text.secondary">
                  Thickness
                </Typography>
                <Slider
                  size="small"
                  min={1}
                  max={8}
                  step={1}
                  value={options.thickness}
                  aria-label="Thickness"
                  onChange={(_, thickness) => onOptions({ thickness })}
                />
                <Typography variant="caption" sx={{ minWidth: 28 }}>
                  {options.thickness} pt
                </Typography>
              </Box>
            </Section>
            <Divider />

            <Section title="Text">
              <ColorRow
                label="Text color"
                value={options.textColor}
                onChange={(textColor) => onOptions({ textColor })}
              />
              <Box display="flex" gap={1} mt={1.5}>
                <Select
                  size="small"
                  value={options.fontSize}
                  inputProps={{ "aria-label": "Font size" }}
                  onChange={(event) =>
                    onOptions({ fontSize: event.target.value })
                  }
                >
                  {FONT_SIZES.map((size) => (
                    <MenuItem key={size} value={size}>
                      {size}
                    </MenuItem>
                  ))}
                </Select>
                <ToggleButtonGroup
                  size="small"
                  value={textStyle}
                  aria-label="Text style"
                  onChange={(_, styles) =>
                    onOptions({
                      bold: styles.includes("bold"),
                      italic: styles.includes("italic"),
                      underline: styles.includes("underline"),
                    })
                  }
                >
                  <ToggleButton value="bold" aria-label="Bold">
                    <FormatBoldIcon fontSize="small" />
                  </ToggleButton>
                  <ToggleButton value="italic" aria-label="Italic">
                    <FormatItalicIcon fontSize="small" />
                  </ToggleButton>
                  <ToggleButton value="underline" aria-label="Underline">
                    <FormatUnderlinedIcon fontSize="small" />
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Section>
            <Divider />

            <Section title="Quick marking">
              <Box display="flex" gap={1} mb={1.25}>
                <Button
                  fullWidth
                  variant={tool === "tick" ? "contained" : "outlined"}
                  color="success"
                  disabled={readOnly}
                  startIcon={<CheckIcon />}
                  onClick={() => onTool("tick")}
                >
                  Tick
                </Button>
                <Button
                  fullWidth
                  variant={tool === "cross" ? "contained" : "outlined"}
                  color="error"
                  disabled={readOnly}
                  startIcon={<CloseIcon />}
                  onClick={() => onTool("cross")}
                >
                  Cross
                </Button>
              </Box>
              <MarkStamps
                tool={tool}
                options={options}
                activeQuestion={activeQuestion}
                disabled={readOnly}
                onTool={onTool}
                onOptions={onOptions}
              />
            </Section>
          </>
        )}

        {tab === 1 && (
          <Section title="Quick comments">
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ mb: 1 }}
            >
              Pick a comment, then click on the page.
            </Typography>
            <PresetList
              items={comments}
              selected={tool === "comment" ? options.commentText : null}
              disabled={readOnly}
              addLabel="Add a comment"
              onPick={pickComment}
              onChange={setComments}
            />
          </Section>
        )}

        {tab === 2 && (
          <>
            <Section title="Circle marks">
              <MarkStamps
                tool={tool}
                options={options}
                activeQuestion={activeQuestion}
                disabled={readOnly}
                onTool={onTool}
                onOptions={onOptions}
              />
              <Tooltip
                arrow
                title="Marks where this question's answer is on the page"
              >
                <span>
                  <Button
                    fullWidth
                    size="small"
                    variant={tool === "qmark" ? "contained" : "outlined"}
                    disabled={readOnly || !activeQuestion}
                    sx={{ mt: 1.25, textTransform: "none" }}
                    onClick={() => onTool("qmark")}
                  >
                    {activeQuestion
                      ? `Add Q${activeQuestion.no} tag`
                      : "Add question tag"}
                  </Button>
                </span>
              </Tooltip>
            </Section>
            <Divider />
            <Section title="Text stamps">
              <PresetList
                items={stamps}
                selected={tool === "stamp" ? options.stampText : null}
                disabled={readOnly}
                addLabel="Add a stamp"
                onPick={pickStamp}
                onChange={setStamps}
              />
            </Section>
          </>
        )}
      </Box>
    </Box>
  );
}
